import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getTestResults, TutorTestResults } from '../services/tutorTestResultsService';

export default function TutorTestResultsPage() {
  const { courseId, testId } = useParams<{ courseId: string; testId: string }>();
  const cId = Number(courseId);
  const tId = Number(testId);
  const navigate = useNavigate();

  const [results, setResults] = useState<TutorTestResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getTestResults(cId, tId)
      .then(setResults)
      .catch(() => setError('Failed to load results'))
      .finally(() => setLoading(false));
  }, [cId, tId]);

  if (loading) return <CircularProgress sx={{ mt: 8, display: 'block', mx: 'auto' }} />;
  if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
  if (!results) return null;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/tutor/courses/${cId}/tests`)}
        sx={{ mb: 2 }}
      >
        Back to Tests
      </Button>

      <Typography variant="h5" gutterBottom>{results.testName} — Results</Typography>

      <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Attempts</Typography>
          <Typography variant="h6">{results.attemptCount}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Students</Typography>
          <Typography variant="h6">{results.studentCount}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Average Score</Typography>
          <Typography variant="h6">
            {results.averageScore !== null ? `${results.averageScore.toFixed(1)}%` : '—'}
          </Typography>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student</TableCell>
              <TableCell align="right">Score</TableCell>
              <TableCell align="right">%</TableCell>
              <TableCell>Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.attempts.map((a) => (
              <TableRow
                key={a.attemptId}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    `/tutor/courses/${cId}/tests/${tId}/results/students/${a.studentId}/attempts/${a.attemptId}`,
                  )
                }
              >
                <TableCell>{a.attemptNumber}</TableCell>
                <TableCell>{a.studentName}</TableCell>
                <TableCell align="right">{a.scoreCorrect}/{a.scoreTotal}</TableCell>
                <TableCell align="right">{a.scorePercent?.toFixed(1)}%</TableCell>
                <TableCell>{new Date(a.submittedAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {results.attempts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No attempts yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
